/**
 * DossierHeader
 * Header du dossier avec titre, statut et boutons d'action
 */
import React from 'react';
import { ArrowLeft, Sparkles, XCircle, Clock, Play, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { STATUS_COLOR } from '../constants';

export const DossierHeader = ({
  dossier,
  generating,
  starting,
  hasAdvancedItems,
  allItemsGenerated,
  canStartGeneration,
  onBack,
  onGeneratePlan,
  onResetPlan,
  onStartGeneration,
  onStartGenerationImmediate,
  onOpenScheduleModal,
  onCancelScheduledGeneration,
}) => {
  return (
    <div className="flex items-center justify-between mb-6 gap-4">
      <Button onClick={onBack} variant="outline" size="sm" data-testid="back-to-list-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
      </Button>
      
      <div className="flex-1 min-w-0 text-center">
        <h1 className="text-2xl font-bold text-cyan-300 font-rajdhani uppercase tracking-wider truncate">
          {dossier.dossier_title || dossier.title}
        </h1>
        <div className="flex items-center justify-center gap-2 mt-1">
          <Badge className={`text-[10px] border ${STATUS_COLOR[dossier.status] || STATUS_COLOR.draft}`}>
            {dossier.status}
          </Badge>
          <span className="text-xs text-slate-500">{dossier.main_keyword}</span>
          {dossier.items?.length > 0 && (
            <span className="text-xs text-emerald-400">
              {dossier.items.filter(i => i.status === 'generated').length}/{dossier.items.length} générés
            </span>
          )}
        </div>
      </div>
      
      <div className="flex gap-2 flex-wrap">
        {/* Générer/Régénérer plan */}
        <Button
          onClick={onGeneratePlan}
          disabled={generating || hasAdvancedItems}
          variant="outline"
          size="sm"
          data-testid="generate-plan-btn"
          title={hasAdvancedItems ? 'Régénération bloquée : articles déjà produits ou publiés' : ''}
        >
          <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-pulse' : ''}`} /> 
          {generating ? 'Génération…' : (dossier.items?.length ? 'Régénérer plan' : 'Générer plan')}
        </Button>
        
        {/* Annuler le plan */}
        {dossier.items?.length > 0 && !hasAdvancedItems && (
          <Button
            onClick={onResetPlan}
            variant="outline"
            size="sm"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            data-testid="reset-plan-btn"
            title="Supprime le plan et revient à la configuration. Les données saisies sont conservées."
          >
            <XCircle className="w-4 h-4 mr-2" /> Annuler le plan
          </Button>
        )}
        
        {/* Lancer génération (programmée 01h) */}
        {canStartGeneration && (
          <Button
            onClick={onStartGeneration}
            disabled={starting}
            className="bg-emerald-600 hover:bg-emerald-500"
            size="sm"
            data-testid="start-generation-btn"
            title="Programme la génération pour la prochaine 01h00 (heures creuses)."
          >
            <Clock className={`w-4 h-4 mr-2 ${starting ? 'animate-pulse' : ''}`} />
            {starting
              ? 'Lancement…'
              : (allItemsGenerated ? 'Relancer (nuit · 01h)' : 'Lancer génération (nuit · 01h)')}
          </Button>
        )}
        
        {/* Générer maintenant */}
        {canStartGeneration && !dossier.scheduled_generation_at && (
          <Button
            onClick={onStartGenerationImmediate}
            disabled={starting}
            variant="outline"
            size="sm"
            className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
            data-testid="start-generation-now-btn"
            title="Exception : lance la génération MAINTENANT, hors heures creuses. Demande confirmation."
          >
            <Play className="w-4 h-4 mr-2" /> Générer maintenant
          </Button>
        )}
        
        {/* Autre date */}
        {canStartGeneration && !dossier.scheduled_generation_at && (
          <Button
            onClick={onOpenScheduleModal}
            disabled={starting}
            variant="outline"
            size="sm"
            className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="schedule-generation-btn"
            title="Choisir une autre date/heure de programmation."
          >
            <Clock className="w-4 h-4 mr-2" /> Autre date
          </Button>
        )}
        
        {/* Badge programmation */}
        {dossier.scheduled_generation_at && (
          <div className="flex items-center gap-1.5">
            <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/40 text-[10px]" data-testid="scheduled-badge">
              <Clock className="w-3 h-3 mr-1 inline" />
              Programmé : {new Date(dossier.scheduled_generation_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
            </Badge>
            <Button
              onClick={onCancelScheduledGeneration}
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-red-400 hover:bg-red-500/10"
              data-testid="cancel-schedule-btn"
              title="Annuler la programmation"
            >
              <XCircle className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
        
        {/* Badge en cours */}
        {dossier.status === 'articles_generating' && (
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]">
            <Loader2 className="w-3 h-3 mr-1 animate-spin inline" /> En cours…
          </Badge>
        )}
      </div>
    </div>
  );
};

export default DossierHeader;
